import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fluxo Co Token Key
const TOKEN_KEY = 'fluxoco_token';

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/saas/stats');

// Clients
export const getClients = () => api.get('/clients');
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Bots
export const getBots = (clientId = null) => {
  const params = clientId ? { client_id: clientId } : {};
  return api.get('/bots', { params });
};
export const getBot = (id) => api.get(`/bots/${id}`);
export const createBot = (data) => api.post('/bots', data);
export const updateBot = (id, data) => api.put(`/bots/${id}`, data);
export const deleteBot = (id) => api.delete(`/bots/${id}`);
export const toggleBotStatus = (id) => api.post(`/bots/${id}/toggle`);

// Conversations
export const getConversations = (filters = {}) => api.get('/conversations', { params: filters });
export const getConversation = (id) => api.get(`/conversations/${id}`);

// Chat
export const sendChatMessage = (botId, message, customerPhone = 'test_user') => 
  api.post('/chat', { bot_id: botId, message, customer_phone: customerPhone });

// Tickets
export const getTickets = (filters = {}) => api.get('/tickets', { params: filters });
export const createTicket = (data) => api.post('/tickets', data);
export const updateTicket = (id, data) => api.put(`/tickets/${id}`, data);

// Master AI
export const sendMasterAIInstruction = (instruction, context = null) => 
  api.post('/master-ai', { instruction, context });
export const createFromSuggestion = (suggestion) => 
  api.post('/master-ai/create-from-suggestion', suggestion);

// Reports
export const generateReport = (params) => api.post('/reports/generate', params);

// Brain Factory (n8n)
export const getN8nStatus = () => api.get('/factory/n8n/status');
export const cloneN8nWorkflow = (data) => api.post('/factory/n8n/clone', data);
export const getN8nWorkflows = () => api.get('/factory/n8n/workflows');
export const getFactoryDiagnosis = () => api.get('/factory/n8n/diagnosis');
export const recloneBotWorkflow = (botId) => api.post(`/factory/n8n/reclone/${botId}`);

// Monitoring
export const getMonitoringStats = () => api.get('/monitoring/stats/summary');
export const getTenantsStats = () => api.get('/monitoring/stats/tenants');
export const getEventFeed = (limit = 100, type = 'all') => 
  api.get('/monitoring/feed', { params: { limit, event_type: type } });
export const triggerTestEvent = () => api.post('/monitoring/test-event');

// Admin & Audit
export const getActivityLogs = () => api.get('/admin/activity-logs');

// Silos / Multi-Tenant Collections Hub
export const getTenantCollections = (tenantId) => api.get(`/tenants/${tenantId}/collections`);
export const createTenantCollection = (tenantId, data) => api.post(`/tenants/${tenantId}/collections`, data);
export const deleteTenantCollection = (tenantId, collectionId) => api.delete(`/tenants/${tenantId}/collections/${collectionId}`);
export const toggleCollectionStatus = (tenantId, collectionId) => api.post(`/tenants/${tenantId}/collections/${collectionId}/toggle`);

export default api;
